// `StatStrip` / `StatTile` (design-system spec §3.4) — the compact KPI strip.
// Label 11 px uppercase, value 20–24 px semibold tabular. NEVER poster
// numerals: 22 px is the office value size and the scale contains nothing
// larger for the office (§1). StatTile is the only sanctioned number-in-a-box.

import type { ReactNode } from "react";
import { Skeleton } from "./states";

export function StatStrip({ children, ...rest }: { children: ReactNode; "data-testid"?: string }) {
  return (
    <div className="ds-kpis" {...rest}>
      {children}
    </div>
  );
}

export interface StatTileTrend {
  /** Rendered arrow + delta, e.g. "▲ 4". */
  text: string;
  /** Semantic direction: a falling scrap rate is `good` (§3.4). */
  tone: "good" | "bad" | "flat";
}

export interface StatTileProps {
  /** Usually a string; a skeleton node while a gating screen reserves (§5). */
  label: ReactNode;
  /** The 22 px tabular value. Pass undefined with `loading` to reserve. */
  value: ReactNode;
  unit?: string | undefined;
  trend?: StatTileTrend | undefined;
  sub?: ReactNode | undefined;
  /** 0–100 renders the 4 px accent meter bar (the setup-progress tile). */
  meter?: number | undefined;
  hint?: string | undefined;
  loading?: boolean;
  "data-testid"?: string;
}

export function StatTile({
  label,
  value,
  unit,
  trend,
  sub,
  meter,
  hint,
  loading = false,
  ...rest
}: StatTileProps) {
  return (
    <div className="ds-kpi" {...rest}>
      <div className="ds-kpi-lb">
        {label}
        {hint !== undefined ? <span className="ds-kpi-hint">{hint}</span> : null}
      </div>
      <div className="ds-kpi-v num">
        {loading ? <Skeleton width="56px" height="22px" /> : value}
        {!loading && unit !== undefined ? <span className="ds-kpi-unit">{unit}</span> : null}
        {!loading && trend !== undefined ? (
          <span
            className={`ds-kpi-tr ${
              trend.tone === "good"
                ? "ds-tr-good"
                : trend.tone === "bad"
                  ? "ds-tr-bad"
                  : "ds-tr-flat"
            }`}
          >
            {trend.text}
          </span>
        ) : null}
      </div>
      {sub !== undefined ? <div className="ds-kpi-sub">{sub}</div> : null}
      {meter !== undefined ? (
        <div className="ds-mbar" role="presentation">
          <i style={{ width: `${Math.max(0, Math.min(100, meter))}%` }} />
        </div>
      ) : null}
    </div>
  );
}
